// src/components/dashboard components/forms/ContactUsEnquiryForm.jsx
import React, { useEffect, useState } from "react";
import { Save } from "lucide-react";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const ContactUsEnquiryForm = ({ apiBase = `${API_BASE_URL}/contact` }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  const [formData, setFormData] = useState({
    title: "",
    subtitle: "",
    description: "",
    phone_number: "",
    email: "",
  });

  const fetchEnquiryForm = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("token");

      const res = await fetch(`${apiBase}/admin/enquiry-form`, {
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      });

      if (!res.ok) throw new Error(`Failed to fetch: ${res.status}`);

      const data = await res.json();
      setRows(Array.isArray(data) ? data : data ? [data] : []);
    } catch (err) {
      console.error("❌ Error fetching enquiry form:", err);
      alert(`Error loading data: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEnquiryForm();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e?.preventDefault?.();

    const payload = {
      enquiry_id: 0,
      title: formData.title.trim(),
      subtitle: formData.subtitle.trim(),
      description: formData.description.trim(),
      phone_number: formData.phone_number.trim(),
      email: formData.email.trim(),
    };

    if (!payload.title) {
      alert("Title is required.");
      return;
    }

    if (payload.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(payload.email)) {
      alert("Please enter a valid email address.");
      return;
    }

    if (payload.phone_number && !/^\d{10}$/.test(payload.phone_number)) {
      alert("Phone number must be 10 digits.");
      return;
    }

    const token = localStorage.getItem("token");

    try {
      const res = await fetch(`${apiBase}/admin/enquiry-form/save`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify(payload),
      });

      if (!res.ok) throw new Error("Failed to save enquiry form.");

      await fetchEnquiryForm();
      alert("Enquiry form configuration saved successfully!");
    } catch (err) {
      console.error("Error saving enquiry form:", err);
      alert("Failed to save enquiry form. Check console.");
    }
  };

  return (
    <section className="bg-white rounded-xl shadow p-8 mb-10">
      <h2 className="text-2xl font-semibold text-blue-700 mb-6">
        Contact Enquiry Form Configuration
      </h2>

      {/* Table */}
      <div className="overflow-x-auto mb-8">
        <table className="min-w-full border border-gray-200 rounded-lg text-sm">
          <thead className="bg-gray-100 text-gray-700">
            <tr>
              <th className="px-6 py-3 text-left font-semibold">Title</th>
              <th className="px-6 py-3 text-left font-semibold">Subtitle</th>
              <th className="px-6 py-3 text-left font-semibold">Phone</th>
              <th className="px-6 py-3 text-left font-semibold">Email</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td
                  colSpan={4}
                  className="text-center py-5 text-gray-500 italic"
                >
                  Loading...
                </td>
              </tr>
            ) : rows.length > 0 ? (
              rows.map((row, index) => (
                <tr
                  key={row.enquiry_id || `enquiry-${index}`}
                  className="border-t hover:bg-gray-50 transition"
                >
                  <td className="px-6 py-3">{row.title}</td>
                  <td className="px-6 py-3">{row.subtitle}</td>
                  <td className="px-6 py-3">{row.phone_number || "—"}</td>
                  <td className="px-6 py-3">{row.email || "—"}</td>
                </tr>
              ))
            ) : (
              <tr>
                <td
                  colSpan={4}
                  className="text-center py-5 text-gray-500 italic"
                >
                  No enquiry form configuration yet
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Direct Form (Always visible) */}
      <form onSubmit={handleSubmit} className="border-t pt-6 mt-6">
        <h3 className="text-lg font-semibold mb-4 text-gray-800">
          Enquiry Form Details
        </h3>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium mb-1">
              Title <span className="text-red-500">*</span>
            </label>
            <input
              name="title"
              value={formData.title}
              onChange={handleChange}
              required
              placeholder="e.g., Enquiry Form"
              className="w-full border rounded-lg px-3 py-2 shadow-sm focus:ring-2 focus:ring-blue-400 outline-none"
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">Subtitle</label>
            <input
              name="subtitle"
              value={formData.subtitle}
              onChange={handleChange}
              placeholder="e.g., Get in touch with us"
              className="w-full border rounded-lg px-3 py-2 shadow-sm focus:ring-2 focus:ring-blue-400 outline-none"
            />
          </div>

          <div className="md:col-span-2">
            <label className="block text-sm font-medium mb-1">
              Description
            </label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleChange}
              rows={3}
              placeholder="e.g., Fill in your details and we'll get back to you"
              className="w-full border rounded-lg px-3 py-2 shadow-sm focus:ring-2 focus:ring-blue-400 outline-none resize-y"
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">
              Phone Number
            </label>
            <input
              type="tel"
              name="phone_number"
              value={formData.phone_number}
              onChange={handleChange}
              placeholder="e.g., 9876543210"
              className="w-full border rounded-lg px-3 py-2 shadow-sm focus:ring-2 focus:ring-blue-400 outline-none"
            />
            <p className="text-xs text-gray-500 mt-1">10 digits only</p>
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">
              Email Address
            </label>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              placeholder="e.g., contact@example.com"
              className="w-full border rounded-lg px-3 py-2 shadow-sm focus:ring-2 focus:ring-blue-400 outline-none"
            />
          </div>
        </div>

        <div className="mt-6 flex justify-end">
          <button
            type="submit"
            className="flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white px-5 py-2 rounded-lg"
          >
            <Save size={18} />
            Save
          </button>
        </div>
      </form>
    </section>
  );
};

export default ContactUsEnquiryForm;
